import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ChildProgress.css';

const ChildProgress = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [childData, setChildData] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState('week');
  const [selectedSubject, setSelectedSubject] = useState('all');

  const fetchChildProgress = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch child basic info
      const childResponse = await axios.get(
        `/api/users/child/${childId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Fetch progress data
      const progressResponse = await axios.get(
        `/api/users/child/${childId}/progress?timeframe=${selectedTimeframe}&subject=${selectedSubject}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setChildData(childResponse.data);
      setProgressData(progressResponse.data);
    } catch (err) {
      console.error('Error fetching child progress:', err);
      setError('Failed to load child progress data');
    } finally {
      setLoading(false);
    }
  }, [childId, selectedTimeframe, selectedSubject]);

  useEffect(() => {
    fetchChildProgress();
  }, [fetchChildProgress]);

  const formatTime = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getPerformanceColor = (score) => {
    if (score >= 80) return '#28a745';
    if (score >= 60) return '#ffc107';
    return '#dc3545';
  };

  const getGradeColor = (grade) => {
    const gradeColors = {
      'A+': '#28a745', 'A': '#28a745', 'A-': '#28a745',
      'B+': '#17a2b8', 'B': '#17a2b8', 'B-': '#17a2b8',
      'C+': '#ffc107', 'C': '#ffc107', 'C-': '#ffc107',
      'D+': '#fd7e14', 'D': '#fd7e14', 'D-': '#fd7e14',
      'F': '#dc3545'
    };
    return gradeColors[grade] || '#6c757d';
  };

  if (loading) {
    return (
      <div className="child-progress">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading child progress...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="child-progress">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>Error Loading Progress</h3>
          <p>{error}</p>
          <button onClick={() => navigate('/parent/manage-children')} className="back-btn">
            ← Back to Children
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="child-progress">
      {/* Header */}
      <div className="progress-header">
        <button onClick={() => navigate('/parent/manage-children')} className="back-btn">
          ← Back to Children
        </button>
        <div className="child-info">
          <div className="child-avatar">
            <img 
              src={childData?.avatar || '/default-avatar.png'} 
              alt={childData?.name}
            />
          </div>
          <div className="child-details">
            <h1>{childData?.name}'s Progress</h1>
            <p className="child-meta">
              Grade {childData?.grade} • {childData?.email}
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Time Period:</label>
          <select 
            value={selectedTimeframe} 
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="filter-select"
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Subject:</label>
          <select 
            value={selectedSubject} 
            onChange={(e) => setSelectedSubject(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Subjects</option>
            <option value="mathematics">Mathematics</option>
            <option value="science">Science</option>
            <option value="english">English</option>
            <option value="history">History</option>
            <option value="geography">Geography</option>
          </select>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="overview-stats">
        <div className="stat-card">
          <div className="stat-icon">📚</div>
          <div className="stat-content">
            <div className="stat-value">{progressData?.totalSessions || 0}</div>
            <div className="stat-label">Study Sessions</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⏱️</div>
          <div className="stat-content">
            <div className="stat-value">{formatTime(progressData?.totalTime || 0)}</div>
            <div className="stat-label">Study Time</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <div className="stat-value" style={{ color: getPerformanceColor(progressData?.averageScore || 0) }}>
              {progressData?.averageScore || 0}%
            </div>
            <div className="stat-label">Average Score</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🏆</div>
          <div className="stat-content">
            <div className="stat-value">{progressData?.completedQuizzes || 0}</div>
            <div className="stat-label">Quizzes Completed</div>
          </div>
        </div>
      </div>

      {/* Subject Performance */}
      <div className="section">
        <h2>Subject Performance</h2>
        <div className="subjects-grid">
          {progressData?.subjectPerformance?.map((subject, index) => (
            <div key={index} className="subject-card">
              <div className="subject-header">
                <h3>{subject.name}</h3>
                <div 
                  className="subject-grade"
                  style={{ backgroundColor: getGradeColor(subject.grade) }}
                >
                  {subject.grade}
                </div>
              </div>
              <div className="subject-stats">
                <div className="subject-stat">
                  <span className="stat-label">Average Score:</span>
                  <span className="stat-value" style={{ color: getPerformanceColor(subject.averageScore) }}>
                    {subject.averageScore}%
                  </span>
                </div>
                <div className="subject-stat">
                  <span className="stat-label">Time Spent:</span>
                  <span className="stat-value">{formatTime(subject.timeSpent)}</span>
                </div>
                <div className="subject-stat">
                  <span className="stat-label">Quizzes:</span>
                  <span className="stat-value">{subject.quizzesCompleted}</span>
                </div>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ 
                    width: `${subject.averageScore}%`,
                    backgroundColor: getPerformanceColor(subject.averageScore)
                  }}
                ></div>
              </div>
            </div>
          )) || []}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="section">
        <h2>Recent Activity</h2>
        <div className="activity-list">
          {progressData?.recentActivity?.length > 0 ? (
            progressData.recentActivity.map((activity, index) => (
              <div key={index} className="activity-item">
                <div className="activity-icon">
                  {activity.type === 'quiz' ? '📝' : 
                   activity.type === 'game' ? '🎮' : 
                   activity.type === 'session' ? '📚' : '📖'}
                </div>
                <div className="activity-content">
                  <div className="activity-title">{activity.title}</div>
                  <div className="activity-details">
                    <span className="activity-subject">{activity.subject}</span>
                    <span className="activity-score" style={{ color: getPerformanceColor(activity.score) }}>
                      {activity.score}%
                    </span>
                  </div>
                </div>
                <div className="activity-time">
                  {new Date(activity.date).toLocaleDateString()}
                </div>
              </div>
            ))
          ) : (
            <div className="no-activity">
              <div className="no-activity-icon">📊</div>
              <h3>No Recent Activity</h3>
              <p>No study activity found for the selected time period.</p>
            </div>
          )}
        </div>
      </div>

      {/* Study Streak */}
      <div className="section">
        <h2>Study Streak</h2>
        <div className="streak-container">
          <div className="streak-info">
            <div className="streak-number">{progressData?.currentStreak || 0}</div>
            <div className="streak-label">Day Streak</div>
          </div>
          <div className="streak-calendar">
            {progressData?.streakCalendar?.map((day, index) => (
              <div 
                key={index} 
                className={`streak-day ${day.hasActivity ? 'active' : ''}`}
                title={`${day.date}: ${day.hasActivity ? 'Studied' : 'No activity'}`}
              >
                <div className="day-number">{day.day}</div>
              </div>
            )) || []}
          </div>
        </div>
      </div>

      {/* Achievements */}
      <div className="section">
        <h2>Recent Achievements</h2>
        <div className="achievements-grid">
          {progressData?.achievements?.length > 0 ? (
            progressData.achievements.map((achievement, index) => (
              <div key={index} className="achievement-card">
                <div className="achievement-icon">{achievement.icon}</div>
                <div className="achievement-content">
                  <h4>{achievement.title}</h4>
                  <p>{achievement.description}</p>
                  <div className="achievement-date">
                    Earned on {new Date(achievement.date).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-achievements">
              <div className="no-achievements-icon">🏆</div>
              <h3>No Achievements Yet</h3>
              <p>Keep studying to unlock achievements!</p>
            </div>
          )}
        </div>
      </div>

      {/* Recommendations */}
      <div className="section">
        <h2>Recommendations</h2>
        <div className="recommendations-list">
          {progressData?.recommendations?.length > 0 ? (
            progressData.recommendations.map((rec, index) => (
              <div key={index} className="recommendation-item">
                <div className="recommendation-icon">{rec.icon}</div>
                <div className="recommendation-content">
                  <h4>{rec.title}</h4>
                  <p>{rec.description}</p>
                </div>
                <div className="recommendation-priority">
                  <span className={`priority-badge ${rec.priority}`}>
                    {rec.priority}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="no-recommendations">
              <div className="no-recommendations-icon">💡</div>
              <h3>No Recommendations</h3>
              <p>Great job! Keep up the excellent work.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChildProgress;